/**
 * Meta Ads module — reuse app theme tokens (see apps/dashboard/src/index.css).
 * Use Tailwind classes: primary, brand-cyan, brand-purple, ring, border, card, etc.
 */

import { buttonVariants } from "@/components/ui/UIElements/Button";
import { cn } from "@/lib/utils";

/** Focus ring shared by Ordering Product controls. */
export const metaAdsInputFocusClass =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/** Primary solid CTA — same pattern as Billing / EmptyBusinessState */
export const metaAdsPrimaryButtonClass = buttonVariants({
  variant: "default",
  size: "touch",
});

/** Text link / accent — same as Link in Bio & widgets */
export const metaAdsLinkClass =
  "text-primary transition-colors hover:text-primary/80 motion-reduce:transition-none";

/** Primary launch action in Ordering Product chrome. */
export const metaAdsGradientCtaClass = metaAdsPrimaryButtonClass;

/** Provider preview retains its existing authored appearance. */
export const metaAdsPreviewCtaClass =
  "bg-primary text-primary-foreground shadow-[var(--od-shadow-1)] transition-colors hover:bg-primary/90 motion-reduce:transition-none";

/** Product headings use ink hierarchy rather than the provider/logo palette. */
export const metaAdsTitleGradientClass = "text-foreground";

/** One-pixel structural frame for hub visuals and small widgets. */
export const metaAdsGradientBorderOnlyClass =
  "rounded-[var(--od-radius-panel)] bg-border p-px";

/** Connect card — same frame as border-only + restrained elevation. */
export const metaAdsGradientCardEdgeClass =
  "rounded-[var(--od-radius-panel)] bg-border p-px shadow-[var(--od-shadow-1)]";

/** Outline / secondary CTA (Continue with Meta, ghost buttons) */
export const metaAdsOutlineCtaClass = buttonVariants({
  variant: "outline",
  size: "touch",
});

/**
 * Select + native textarea only — same surface as `Input` variant="dark" (BusinessCreationForm).
 * @see components/ui/UIElements/Input — dark: border-gray-600 bg-gray-800, focus:ring-blue-500
 */
export const metaAdsSelectClass = cn(
  "custom-select flex h-11 w-full cursor-pointer rounded-md border border-input bg-background px-3 text-base text-foreground",
  "shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
);

export const metaAdsTextareaClass = cn(
  "min-h-[120px] w-full resize-none rounded-md border border-input bg-background px-3 py-3 text-base text-foreground placeholder:text-muted-foreground shadow-sm",
  "transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
);

/** Section card surface (forms) */
export const metaAdsFormSectionClass =
  "rounded-[var(--od-radius-panel)] border border-border bg-card p-5";

export const metaAdsFormSectionLabelClass =
  "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

/** Field labels — matches store create form (BusinessCreationForm) */
export const metaAdsFormFieldLabelClass =
  "text-[length:var(--font-size-product-ui)] font-medium text-foreground";

/** Geometry only: semantic danger presentation belongs to Button emphasis/intent. */
export const metaAdsDestructiveIconButtonClass = "h-11 w-11 shrink-0";
export const metaAdsDangerZoneDeleteButtonClass =
  "w-full min-h-11 sm:min-w-[11rem]";
export const metaAdsDestructiveSolidConfirmButtonClass = buttonVariants({
  variant: "destructive",
  size: "touch",
});
